import { useDarkMode } from '../App'
import { useTranslation } from '../i18n/translations'
import { useLanguage } from '../contexts/LanguageContext'

export default function About(){
  const { isDark } = useDarkMode()
  const { t } = useTranslation()
  const { isRTL } = useLanguage()

  return (
    <div className={`min-h-screen w-full transition-colors ${isDark ? 'text-white' : 'text-gray-900'} ${isRTL ? 'font-mixed rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Hero Section - consistent with other pages */}
      <div className="relative overflow-hidden">
        <div className={`absolute inset-0 transition-colors ${
          isDark ? 'bg-gradient-to-br from-emerald-900/10 to-cyan-900/10' : 'bg-gradient-to-br from-blue-50/30 to-purple-50/30'
        }`}></div>
        <div className={`relative max-w-7xl mx-auto px-4 ${isRTL ? 'pt-40 pb-20' : 'py-20'}`}>
          <div className={`text-center ${isRTL ? 'rtl' : ''}`}>
            <div className={`inline-flex items-center px-4 py-2 border rounded-full text-sm font-medium mb-8 transition-colors ${
              isDark 
                ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' 
                : 'bg-blue-50 border-blue-200 text-blue-600'
            } ${isRTL ? 'flex-row-reverse' : ''}`}>
              <svg className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'} ${isDark ? 'text-emerald-400' : 'text-blue-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t('about.badge')}
            </div>
            
            <h1 className={`text-4xl md:text-5xl font-bold mb-6 transition-colors ${
              isDark 
                ? 'bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent' 
                : 'text-gray-900'
            } ${isRTL ? 'font-mixed' : ''}`}>
              {t('about.mission_title')}
            </h1>
            <p className={`text-xl mb-12 max-w-2xl mx-auto transition-colors ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {t('about.mission_subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className={`prose prose-lg max-w-none transition-colors ${
          isDark ? 'prose-invert' : ''
        }`}>
          
          <div className={`border rounded-2xl p-8 mb-12 transition-colors ${
            isDark 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-white border-gray-200'
          } ${isRTL ? 'force-rtl' : 'force-ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
            <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'} ${isRTL ? 'font-mixed force-rtl' : 'force-ltr'}`}>
              {t('about.what_we_do_title')}
            </h2>
            <p className={`text-lg leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'} ${isRTL ? 'force-rtl' : 'force-ltr'}`}>
              {t('about.what_we_do_desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className={`border rounded-xl p-6 transition-colors ${
              isDark 
                ? 'bg-gray-800 border-gray-700' 
                : 'bg-white border-gray-200'
            }`} dir={isRTL ? 'rtl' : 'ltr'}>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
                isDark ? 'bg-emerald-500/20' : 'bg-blue-100'
              }`}>
                <svg className={`w-6 h-6 ${isDark ? 'text-emerald-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'} ${isRTL ? 'font-mixed force-rtl' : 'force-ltr'}`}>
                {t('about.free_access_title')}
              </h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} ${isRTL ? 'force-rtl' : 'force-ltr'}`}>
                {t('about.free_access_desc')}
              </p>
            </div>

            <div className={`border rounded-xl p-6 transition-colors ${
              isDark 
                ? 'bg-gray-800 border-gray-700' 
                : 'bg-white border-gray-200'
            }`} dir={isRTL ? 'rtl' : 'ltr'}>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
                isDark ? 'bg-cyan-500/20' : 'bg-green-100'
              }`}>
                <svg className={`w-6 h-6 ${isDark ? 'text-cyan-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'} ${isRTL ? 'font-mixed force-rtl' : 'force-ltr'}`}>
                {t('about.quality_curated_title')}
              </h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} ${isRTL ? 'force-rtl' : 'force-ltr'}`}>
                {t('about.quality_curated_desc')}
              </p>
            </div>
          </div>

          <div className={`border rounded-2xl p-8 transition-colors ${
            isDark 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-white border-gray-200'
          }`} dir={isRTL ? 'rtl' : 'ltr'}>
            <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'} ${isRTL ? 'font-mixed force-rtl' : 'force-ltr'}`}>
              {t('about.commitment_title')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <div className={`text-3xl font-bold mb-2 ${isDark ? 'text-emerald-400' : 'text-blue-600'}`}>
                  10,000+
                </div>
                <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('about.stats.resources')}
                </div>
              </div>
              <div>
                <div className={`text-3xl font-bold mb-2 ${isDark ? 'text-emerald-400' : 'text-blue-600'}`}>
                  100%
                </div>
                <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('about.stats.free_access')}
                </div>
              </div>
              <div>
                <div className={`text-3xl font-bold mb-2 ${isDark ? 'text-emerald-400' : 'text-blue-600'}`}>
                  24/7
                </div>
                <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('about.stats.always_available')}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}